import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Button,
  Box,
  CircularProgress,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import SortIcon from '@mui/icons-material/Sort';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { jwtDecode } from 'jwt-decode';
import { API_BASE_URL } from '../../constants/constants';
import { DataGrid } from '@mui/x-data-grid';
import BackButton from '../../components/BackButton';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
// [CASH-MODE] UPI / Cash filter + indication (credits / manual rows have no mode -> '-')
import PaymentModeFilter from '../../components/PaymentModeFilter';
import PaymentModeChip from '../../components/PaymentModeChip';
import PaymentModeTotals from '../../components/PaymentModeTotals';
import { PAYMENT_MODES } from '../../constants/paymentModes';
import { paymentModeParam, paymentModeLabel, rowPaymentMode, totalsByModeFromRows } from '../../utils/paymentMode';

// [CASH-MODE] '-' when the row carries no payment mode
const advanceModeLabel = (row) => (row && row.paymentMode ? paymentModeLabel(row) : '-');

const MerchantsAdvanceFundReport = () => {
  const navigate = useNavigate();

  const [startDate, setStartDate] = useState(
    new Date(new Date().setDate(new Date().getDate() - 30))
  );
  const [endDate, setEndDate] = useState(new Date());
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userLevel, setUserLevel] = useState(null);
  const [userId, setUserId] = useState('');

  // Selected Detail Modal State
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [openDetailModal, setOpenDetailModal] = useState(false);

  // Dropdown lists
  const [allStateHeads, setAllStateHeads] = useState([]);
  const [allQpayCenters, setAllQpayCenters] = useState([]);
  const [allDistrictsList, setAllDistrictsList] = useState([]);
  const [allConstituenciesList, setAllConstituenciesList] = useState([]);

  // Filters State
  const [districtFilter, setDistrictFilter] = useState('all');
  const [villageFilter, setVillageFilter] = useState('all'); // Constituency
  const [stateHeadFilter, setStateHeadFilter] = useState('all');
  const [qpayCenterFilter, setQpayCenterFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('desc'); // 'desc' or 'asc'
  const [paymentMode, setPaymentMode] = useState(PAYMENT_MODES.ALL); // [CASH-MODE]
  const [apiTotalsByMode, setApiTotalsByMode] = useState(null); // [CASH-MODE]

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        setUserLevel(decodedToken.level);
        setUserId(decodedToken.userId);
      } catch (e) {
        console.error('Error decoding token:', e);
      }
    }
  }, []);

  // Fetch dropdown data
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        if (userId) {
          const res = await axios.get(`${API_BASE_URL}/users-with-pincodes/${userId}`);
          if (res.status === 200 && res.data) {
            setAllStateHeads(res.data.stateHeads || []);
            setAllQpayCenters(res.data.qpayCenters || []);
          }
        }
      } catch (err) {
        console.error('Error fetching users dropdown data:', err);
      }

      try {
        const distRes = await axios.get(`${API_BASE_URL}/districts`);
        if (distRes.status === 200 && distRes.data) {
          const list = Array.isArray(distRes.data)
            ? distRes.data
            : distRes.data.districts || [];
          setAllDistrictsList(list);
        }
      } catch (err) {
        console.error('Error fetching districts dropdown data:', err);
      }

      try {
        const shopRes = await axios.get(
          `${API_BASE_URL}/shops/${userId}?progress=6&page=1&limit=500`
        );
        if (shopRes.status === 200 && shopRes.data && shopRes.data.shops) {
          const cons = [
            ...new Set(
              shopRes.data.shops
                .map((s) => s.constituency || s.village || s.city || s.taluk)
                .filter(Boolean)
            ),
          ];
          setAllConstituenciesList(cons.sort());
        }
      } catch (err) {
        console.error('Error fetching shops constituencies dropdown data:', err);
      }
    };

    fetchDropdownData();
  }, [userId]);

  // Fetch Report Data
  const fetchReportData = useCallback(async () => {
    setLoading(true);
    try {
      const formattedStartDate = format(startDate, 'yyyy-MM-dd');
      const formattedEndDate = format(endDate, 'yyyy-MM-dd');

      const payload = {
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        district: districtFilter,
        village: villageFilter,
        stateHeadId: stateHeadFilter,
        qpayCenterId: qpayCenterFilter,
        search: searchQuery,
        sortOrder: sortOrder,
        paymentMode: paymentModeParam(paymentMode), // [CASH-MODE] undefined for ALL
      };

      const response = await axios.post(
        `${API_BASE_URL}/shopReport/merchantsAdvanceFundReport`,
        payload
      );

      if (response.data && response.data.status === 'SUCCESS' && Array.isArray(response.data.data)) {
        // [CASH-MODE] safety net: a selected mode keeps only rows of that mode
        setReportData(
          paymentModeParam(paymentMode)
            ? response.data.data.filter((r) => r.paymentMode && rowPaymentMode(r) === paymentMode)
            : response.data.data
        );
        setApiTotalsByMode(response.data.totalsByMode || null); // [CASH-MODE]
      } else {
        setReportData([]);
        setApiTotalsByMode(null); // [CASH-MODE]
        setDialogMessage('No records found for the selected criteria.');
        setOpenDialog(true);
      }
    } catch (error) {
      console.error('Error fetching merchants advance fund report:', error);
      setDialogMessage('Failed to fetch advance fund report. Please try again.');
      setOpenDialog(true);
      setReportData([]);
      setApiTotalsByMode(null); // [CASH-MODE]
    } finally {
      setLoading(false);
    }
  }, [
    startDate,
    endDate,
    districtFilter,
    villageFilter,
    stateHeadFilter,
    qpayCenterFilter,
    searchQuery,
    sortOrder,
    paymentMode, // [CASH-MODE]
  ]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleOpenDetailModal = (row) => {
    setSelectedRecord(row);
    setOpenDetailModal(true);
  };

  const handleCloseDetailModal = () => {
    setOpenDetailModal(false);
    setSelectedRecord(null);
  };

  const toggleSortOrder = () => {
    setSortOrder((prev) => (prev === 'desc' ? 'asc' : 'desc'));
  };

  // CSV Export Function (SuperAdmin Only)
  const handleExportCSV = () => {
    if (reportData.length === 0) {
      alert('No data available to export');
      return;
    }

    const headers = [
      'SL No',
      'Date & Time',
      'Merchant ID',
      'Merchant Name',
      'Shop Name',
      'Mobile Number',
      'Email ID',
      'District',
      'Constituency/Village',
      'Type',
      'Channel (Intent/Direct)', // [CASH-MODE] was 'Show Mode'
      'Payment Mode', // [CASH-MODE]
      'Amount',
      'Current Balance',
      'Description',
    ];

    const rows = reportData.map((item) => [
      item.slNo,
      item.date ? format(new Date(item.date), 'dd-MM-yyyy HH:mm:ss') : 'N/A',
      `"${item.merchantId || ''}"`,
      `"${item.merchantName || ''}"`,
      `"${item.shopName || ''}"`,
      `"${item.mobile || ''}"`,
      `"${item.email || ''}"`,
      `"${item.district || ''}"`,
      `"${item.village || ''}"`,
      `"${item.type || ''}"`,
      `"${item.showMode || ''}"`,
      `"${advanceModeLabel(item)}"`, // [CASH-MODE]
      item.amount,
      item.currentBalance,
      `"${item.description || ''}"`,
    ]);

    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute(
      'download',
      `Merchants_Advance_Fund_Report_${format(new Date(), 'yyyy-MM-dd')}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // PDF Export Function
  const handleExportPDF = () => {
    if (reportData.length === 0) {
      alert('No data available to export');
      return;
    }

    const doc = new jsPDF('landscape');

    doc.setFontSize(16);
    doc.text('Merchants Advance Fund Report', 14, 15);
    doc.setFontSize(10);
    doc.text(
      `Generated on: ${format(new Date(), 'dd-MM-yyyy HH:mm:ss')} | Date Range: ${format(
        startDate,
        'dd-MM-yyyy'
      )} to ${format(endDate, 'dd-MM-yyyy')}`,
      14,
      22
    );

    const tableColumn = [
      'SL No',
      'Date & Time',
      'Merchant ID',
      'Shop Name',
      'Type',
      'Channel (Intent/Direct)', // [CASH-MODE] was 'Show Mode'
      'Payment Mode', // [CASH-MODE]
      'Amount (₹)',
      'Current Balance (₹)',
    ];

    const tableRows = reportData.map((item) => [
      item.slNo,
      item.date ? format(new Date(item.date), 'dd-MM-yyyy HH:mm:ss') : 'N/A',
      item.merchantId || 'N/A',
      item.shopName || 'N/A',
      item.type || 'N/A',
      item.showMode || 'N/A',
      advanceModeLabel(item), // [CASH-MODE]
      `₹${(item.amount || 0).toLocaleString('en-IN')}`,
      `₹${(item.currentBalance || 0).toLocaleString('en-IN')}`,
    ]);

    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 28,
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: [25, 118, 210] },
    });

    doc.save(
      `Merchants_Advance_Fund_Report_${format(new Date(), 'yyyy-MM-dd')}.pdf`
    );
  };

  // Print Function
  const handlePrint = () => {
    if (reportData.length === 0) {
      alert('No data available to print');
      return;
    }

    const printWindow = window.open('', '_blank');
    const tableHtml = `
      <html>
        <head>
          <title>Merchants Advance Fund Report</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; color: #333; }
            h2 { color: #1976d2; margin-bottom: 5px; }
            p { font-size: 13px; color: #666; margin-top: 0; }
            table { width: 100%; border-collapse: collapse; margin-top: 15px; font-size: 12px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #1976d2; color: white; }
            tr:nth-child(even) { background-color: #f9f9f9; }
            .credit { color: #2e7d32; font-weight: bold; }
            .debit { color: #d32f2f; font-weight: bold; }
          </style>
        </head>
        <body>
          <h2>Merchants Advance Fund Report</h2>
          <p>Generated on: ${format(new Date(), 'dd-MM-yyyy HH:mm:ss')} | Date Range: ${format(
      startDate,
      'dd-MM-yyyy'
    )} to ${format(endDate, 'dd-MM-yyyy')}</p>
          <table>
            <thead>
              <tr>
                <th>SL No</th>
                <th>Date & Time</th>
                <th>Merchant ID</th>
                <th>Merchant Name</th>
                <th>Shop Name</th>
                <th>Type</th>
                <th>Channel (Intent/Direct)</th>
                <th>Payment Mode</th>
                <th>Amount</th>
                <th>Current Balance</th>
              </tr>
            </thead>
            <tbody>
              ${reportData
                .map(
                  (item) => `
                <tr>
                  <td>${item.slNo}</td>
                  <td>${
                    item.date
                      ? format(new Date(item.date), 'dd-MM-yyyy HH:mm:ss')
                      : 'N/A'
                  }</td>
                  <td>${item.merchantId || 'N/A'}</td>
                  <td>${item.merchantName || 'N/A'}</td>
                  <td>${item.shopName || 'N/A'}</td>
                  <td class="${(item.type || '').toLowerCase()}">${item.type}</td>
                  <td>${item.showMode || 'N/A'}</td>
                  <td>${advanceModeLabel(item)}</td>
                  <td>₹${(item.amount || 0).toLocaleString('en-IN')}</td>
                  <td>₹${(item.currentBalance || 0).toLocaleString('en-IN')}</td>
                </tr>
              `
                )
                .join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;

    printWindow.document.write(tableHtml);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  const columns = [
    {
      field: 'slNo',
      headerName: 'SL No',
      width: 80,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'date',
      headerName: 'Date & Time',
      width: 170,
      renderCell: (params) =>
        params.value
          ? format(new Date(params.value), 'dd-MM-yyyy HH:mm:ss')
          : 'N/A',
    },
    {
      field: 'merchantId',
      headerName: 'Merchant ID',
      width: 160,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'type',
      headerName: 'Type (Credit/Debit)',
      width: 160,
      renderCell: (params) => {
        const isCredit = (params.value || '').toUpperCase() === 'CREDIT';
        return (
          <Chip
            label={params.value || 'N/A'}
            color={isCredit ? 'success' : 'error'}
            variant="filled"
            size="small"
            sx={{ fontWeight: 'bold' }}
          />
        );
      },
    },
    {
      field: 'showMode',
      headerName: 'Channel (Intent/Direct)', // [CASH-MODE] renamed; UPI/Cash is the column below
      width: 210,
      renderCell: (params) => (
        <Chip
          label={params.value || 'Direct'}
          color="primary"
          variant="outlined"
          size="small"
        />
      ),
    },
    // [CASH-MODE] UPI / Cash ('-' for rows without a mode, e.g. top-ups)
    {
      field: 'paymentMode',
      headerName: 'Payment Mode',
      width: 130,
      renderCell: (params) => (params.row.paymentMode ? <PaymentModeChip row={params.row} /> : '-'),
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
          ₹{(params.value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
        </Typography>
      ),
    },
    {
      field: 'currentBalance',
      headerName: 'Current balance',
      width: 160,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#0284c7' }}>
          ₹{(params.value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
        </Typography>
      ),
    },
    {
      field: 'action',
      headerName: 'Action Button',
      width: 130,
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Tooltip title="View Merchant Advance Details">
          <IconButton
            color="primary"
            size="small"
            onClick={() => handleOpenDetailModal(params.row)}
          >
            <VisibilityIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box sx={{ padding: 3, backgroundColor: '#f4f6f8', minHeight: '100vh' }}>
      <BackButton />
      <br />
      <Paper elevation={3} sx={{ padding: 3, borderRadius: 2, mb: 3 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
          flexWrap="wrap"
          gap={2}
        >
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
            Merchants Advance Fund Report
          </Typography>

          <Box display="flex" gap={1.5} flexWrap="wrap">
            {userLevel === 1 && (
              <Button
                variant="contained"
                color="success"
                startIcon={<DownloadIcon />}
                onClick={handleExportCSV}
              >
                CSV
              </Button>
            )}
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Filters Section */}
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2.4}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                renderInput={(params) => <TextField {...params} fullWidth size="small" />}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                renderInput={(params) => <TextField {...params} fullWidth size="small" />}
              />
            </Grid>

            {/* District Filter */}
            <Grid item xs={12} sm={6} md={2.4}>
              <FormControl fullWidth size="small">
                <InputLabel id="district-label">District wise</InputLabel>
                <Select
                  labelId="district-label"
                  value={districtFilter}
                  label="District wise"
                  onChange={(e) => setDistrictFilter(e.target.value)}
                >
                  <MenuItem value="all">All Districts</MenuItem>
                  {allDistrictsList.map((dist) => {
                    const name = typeof dist === 'string' ? dist : dist.districtName || dist.name;
                    return (
                      <MenuItem key={name} value={name}>
                        {name}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>

            {/* Constituency Filter */}
            <Grid item xs={12} sm={6} md={2.4}>
              <FormControl fullWidth size="small">
                <InputLabel id="constituency-label">Constituency wise</InputLabel>
                <Select
                  labelId="constituency-label"
                  value={villageFilter}
                  label="Constituency wise"
                  onChange={(e) => setVillageFilter(e.target.value)}
                >
                  <MenuItem value="all">All Constituencies</MenuItem>
                  {allConstituenciesList.map((cons) => (
                    <MenuItem key={cons} value={cons}>
                      {cons}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* State Head Filter */}
            <Grid item xs={12} sm={6} md={2.4}>
              <FormControl fullWidth size="small">
                <InputLabel id="state-head-label">State Head wise</InputLabel>
                <Select
                  labelId="state-head-label"
                  value={stateHeadFilter}
                  label="State Head wise"
                  onChange={(e) => setStateHeadFilter(e.target.value)}
                >
                  <MenuItem value="all">All State Heads</MenuItem>
                  {allStateHeads.map((sh) => (
                    <MenuItem key={sh.id || sh.userId} value={sh.customId || sh.id || sh.userId}>
                      {sh.name} ({sh.customId || sh.id || sh.userId})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Q-Pay Centre Filter */}
            <Grid item xs={12} sm={6} md={2.4}>
              <FormControl fullWidth size="small">
                <InputLabel id="qpay-centre-label">Q-Pay Centre wise</InputLabel>
                <Select
                  labelId="qpay-centre-label"
                  value={qpayCenterFilter}
                  label="Q-Pay Centre wise"
                  onChange={(e) => setQpayCenterFilter(e.target.value)}
                >
                  <MenuItem value="all">All Q-Pay Centres</MenuItem>
                  {allQpayCenters.map((qc) => (
                    <MenuItem key={qc.id || qc.userId} value={qc.customId || qc.id || qc.userId}>
                      {qc.name} ({qc.customId || qc.id || qc.userId})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Search Input */}
            <Grid item xs={12} sm={6} md={3.6}>
              <TextField
                fullWidth
                size="small"
                label="Search by Merchant ID / Mobile / Email"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  endAdornment: <SearchIcon color="action" />,
                }}
              />
            </Grid>

            {/* [CASH-MODE] Payment Mode filter */}
            <Grid item xs={12} sm={6} md={2.4}>
              <PaymentModeFilter value={paymentMode} onChange={setPaymentMode} fullWidth />
            </Grid>

            {/* Sort Toggle */}
            <Grid item xs={12} sm={6} md={2.4}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<SortIcon />}
                onClick={toggleSortOrder}
                sx={{ height: 40 }}
              >
                Sort: {sortOrder === 'desc' ? 'Newest First' : 'Oldest First'}
              </Button>
            </Grid>
          </Grid>
        </LocalizationProvider>

        {/* [CASH-MODE] UPI vs Cash debits (rows with a mode only) */}
        <PaymentModeTotals
          totals={(apiTotalsByMode && apiTotalsByMode.UPI && apiTotalsByMode.UPI.amount !== undefined ? apiTotalsByMode : null) || totalsByModeFromRows(reportData.filter((r) => r.paymentMode), (r) => r.amount)}
          amountKey="amount"
          title="Advance Used"
          sx={{ mt: 2 }}
        />
      </Paper>

      {/* Data Table */}
      <Paper elevation={3} sx={{ height: 600, width: '100%', borderRadius: 2 }}>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
          >
            <CircularProgress />
            <Typography variant="body1" sx={{ ml: 2 }}>
              Loading Merchants Advance Fund Report...
            </Typography>
          </Box>
        ) : (
          <DataGrid
            rows={reportData}
            columns={columns}
            getRowId={(row) => row.id || row.slNo}
            rowHeight={60}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50, 100]}
            disableSelectionOnClick
            sx={{
              border: 'none',
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f8fafc',
                borderBottom: '1px solid #e2e8f0',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 700,
                color: '#1e293b',
                fontSize: 14,
              },
              '& .MuiDataGrid-row:nth-of-type(even)': {
                backgroundColor: '#f8fafc',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: '#f1f5f9',
              },
              '& .MuiDataGrid-footerContainer': {
                borderTop: '1px solid #e2e8f0',
                backgroundColor: '#f8fafc',
              },
            }}
          />
        )}
      </Paper>

      {/* Detail Dialog Modal */}
      <Dialog
        open={openDetailModal}
        onClose={handleCloseDetailModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            backgroundColor: '#1976d2',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          Merchant Advance Detail
          <IconButton
            aria-label="close"
            onClick={handleCloseDetailModal}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          {selectedRecord && (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Merchant ID
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {selectedRecord.merchantId}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Date & Time
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {selectedRecord.date
                    ? format(
                        new Date(selectedRecord.date),
                        'dd-MM-yyyy HH:mm:ss'
                      )
                    : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Merchant Name
                </Typography>
                <Typography variant="body1">
                  {selectedRecord.merchantName}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Shop Name
                </Typography>
                <Typography variant="body1">
                  {selectedRecord.shopName}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Mobile Number
                </Typography>
                <Typography variant="body1">
                  {selectedRecord.mobile}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Email ID
                </Typography>
                <Typography variant="body1">
                  {selectedRecord.email}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  District
                </Typography>
                <Typography variant="body1">
                  {selectedRecord.district}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Constituency
                </Typography>
                <Typography variant="body1">
                  {selectedRecord.village}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Transaction Type
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {selectedRecord.type}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  {/* [CASH-MODE] was 'Show Mode' */}
                  Channel (Intent/Direct)
                </Typography>
                <Typography variant="body1">
                  {selectedRecord.showMode}
                </Typography>
              </Grid>
              {/* [CASH-MODE] */}
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Payment Mode
                </Typography>
                <Box>
                  {selectedRecord.paymentMode ? <PaymentModeChip row={selectedRecord} /> : '-'}
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Amount
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 'bold', color: '#2e7d32' }}
                >
                  ₹{(selectedRecord.amount || 0).toLocaleString('en-IN')}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Current Balance
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 'bold', color: '#0284c7' }}
                >
                  ₹{(selectedRecord.currentBalance || 0).toLocaleString('en-IN')}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Description
                </Typography>
                <Typography variant="body2">
                  {selectedRecord.description || 'N/A'}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDetailModal} variant="contained" color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Info Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Notification</DialogTitle>
        <DialogContent>
          <Typography>{dialogMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MerchantsAdvanceFundReport;
